import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BENEFITS, LIFE_GALLERY } from "@/lib/data";

export default function Life() {
  return (
    <div data-testid="life-page" className="bg-bone">
      {/* HERO */}
      <section className="pt-20 md:pt-28 pb-16 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline text-vermilion mb-8">Life at ThinkRight</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Built by engineers,
              <br />
              for engineers who still
              <br />
              <span className="italic text-ink/60">read the paper.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l border-border">
            <p className="text-lg text-ink/70 leading-relaxed">
              We are senior-heavy on purpose. Process where it counts, ceremony only
              where it helps. No status theatre, no credential worship — just the
              work, done very well.
            </p>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-16 md:py-20">
        <div className="tr-container">
          <div className="grid grid-cols-12 gap-4 md:gap-6 auto-rows-[180px] md:auto-rows-[240px]">
            {LIFE_GALLERY.map((g, i) => (
              <Reveal
                key={g.url}
                delay={i % 4}
                className={`${g.span} overflow-hidden`}
              >
                <img
                  src={g.url}
                  alt={g.alt}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.05]"
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="tr-container">
          <div className="grid grid-cols-12 gap-10 mb-16">
            <div className="col-span-12 md:col-span-5">
              <div className="overline text-vermilion mb-6">What we offer</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                Thoughtful, not
                <br />
                <span className="italic text-ink/60">performative.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-12 border-t border-border">
            {BENEFITS.map((b, i) => (
              <Reveal
                key={b.title}
                delay={i}
                className={`col-span-12 md:col-span-6 lg:col-span-4 border-b border-border ${
                  (i + 1) % 3 !== 0 ? "lg:border-r" : ""
                } ${i % 2 === 0 ? "md:border-r lg:border-r" : ""} p-10`}
              >
                <div className="font-mono text-xs text-vermilion mb-4">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">
                  {b.title}
                </h3>
                <p className="text-sm text-ink/70 leading-relaxed">{b.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-ink text-white">
        <div className="tr-container grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-vermilion mb-6">Join us</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              If this sounds like the
              <br />
              workplace you keep describing
              <br />
              <span className="italic text-white/60">to your friends.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link
              to="/careers"
              data-testid="life-cta-careers"
              className="inline-flex items-center gap-2 bg-white text-ink px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-vermilion hover:text-white transition-colors"
            >
              See open roles <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
